/**
 * CelebrationsPage - Birthday & Anniversary Management
 * - View upcoming celebrations (birthday, marriage anniversary, work anniversary)
 * - Manage wish templates
 * - Send manual wishes
 * - Configure reminder settings
 */
import { useState, useMemo } from 'react';
import { celebrationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApi, useMutation } from '../hooks/useApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, Cake, Heart, Briefcase, Send, Settings, FileText,
  Calendar, Users, Bell, Gift, PartyPopper, RefreshCw, Plus, Edit, Trash2
} from 'lucide-react';

// Celebration type config
const CELEBRATION_TYPES = {
  birthday: { label: 'Birthday', icon: Cake, color: 'text-pink-500', bg: 'bg-pink-50', emoji: '🎂' },
  marriage_anniversary: { label: 'Marriage Anniversary', icon: Heart, color: 'text-red-500', bg: 'bg-red-50', emoji: '💍' },
  work_anniversary: { label: 'Work Anniversary', icon: Briefcase, color: 'text-blue-500', bg: 'bg-blue-50', emoji: '🎉' }
};

export default function CelebrationsPage() {
  const { isHR, isAdmin, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedType, setSelectedType] = useState('all');
  const [daysAhead, setDaysAhead] = useState(30);
  
  // Template management state
  const [templateDialog, setTemplateDialog] = useState({ open: false, template: null, mode: 'create' });
  const [templateForm, setTemplateForm] = useState({ name: '', celebration_type: 'birthday', subject: '', message: '' });
  
  // Send wish state
  const [wishDialog, setWishDialog] = useState({ open: false, celebration: null });
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  
  // Settings state
  const [settingsDialog, setSettingsDialog] = useState(false);
  
  const canManage = isHR || isAdmin || isSuperAdmin;

  // Fetch upcoming celebrations
  const { data: upcomingData, loading: loadingUpcoming, refresh: refreshUpcoming } = useApi(
    () => celebrationsAPI.getUpcoming(daysAhead, selectedType === 'all' ? undefined : selectedType),
    { immediate: true, deps: [daysAhead, selectedType] }
  );

  // Fetch stats
  const { data: stats, loading: loadingStats, refresh: refreshStats } = useApi(
    celebrationsAPI.getStats,
    { immediate: true }
  );

  // Fetch templates
  const { data: templates, loading: loadingTemplates, refresh: refreshTemplates } = useApi(
    () => celebrationsAPI.getTemplates(undefined, false),
    { immediate: canManage }
  );

  // Fetch settings
  const { data: settings, loading: loadingSettings, refresh: refreshSettings } = useApi(
    celebrationsAPI.getSettings,
    { immediate: canManage }
  );

  // Initialize templates mutation
  const { mutate: initializeTemplates, loading: initializing } = useMutation(
    celebrationsAPI.initializeDefaults,
    { successMessage: 'Default templates initialized', onSuccess: () => refreshTemplates() }
  );

  // Create/Update template mutation
  const { mutate: saveTemplate, loading: savingTemplate } = useMutation(
    (data) => templateDialog.mode === 'create' 
      ? celebrationsAPI.createTemplate(data) 
      : celebrationsAPI.updateTemplate(templateDialog.template?.id, data),
    { 
      successMessage: templateDialog.mode === 'create' ? 'Template created' : 'Template updated',
      onSuccess: () => { setTemplateDialog({ open: false, template: null, mode: 'create' }); refreshTemplates(); }
    }
  );

  // Delete template mutation
  const { mutate: deleteTemplate, loading: deletingTemplate } = useMutation(
    (id) => celebrationsAPI.deleteTemplate(id),
    { successMessage: 'Template deleted', onSuccess: () => refreshTemplates() }
  );

  // Send wish mutation
  const { mutate: sendWish, loading: sendingWish } = useMutation(
    ({ employeeId, celebrationType, templateId }) => celebrationsAPI.sendWish(employeeId, celebrationType, templateId),
    { successMessage: 'Wish sent successfully!', onSuccess: () => setWishDialog({ open: false, celebration: null }) }
  );

  // Update settings mutation
  const { mutate: updateSettings, loading: updatingSettings } = useMutation(
    celebrationsAPI.updateSettings,
    { successMessage: 'Settings updated', onSuccess: () => { setSettingsDialog(false); refreshSettings(); } }
  );

  // Send reminders mutation
  const { mutate: sendReminders, loading: sendingReminders } = useMutation(
    () => celebrationsAPI.sendReminders(1),
    { successMessage: 'Reminders sent' }
  );

  // Group upcoming by days
  const groupedUpcoming = useMemo(() => {
    if (!upcomingData?.upcoming) return { today: [], thisWeek: [], later: [] };
    
    const today = upcomingData.upcoming.filter(c => c.days_until === 0);
    const thisWeek = upcomingData.upcoming.filter(c => c.days_until > 0 && c.days_until <= 7);
    const later = upcomingData.upcoming.filter(c => c.days_until > 7);
    
    return { today, thisWeek, later };
  }, [upcomingData]);

  // Templates by type
  const templatesByType = useMemo(() => {
    if (!templates) return {};
    const grouped = {};
    for (const t of templates) {
      if (!grouped[t.celebration_type]) grouped[t.celebration_type] = [];
      grouped[t.celebration_type].push(t);
    }
    return grouped;
  }, [templates]);

  // Handle template edit
  const handleEditTemplate = (template) => {
    setTemplateForm({
      name: template.name,
      celebration_type: template.celebration_type,
      subject: template.subject,
      message: template.message
    });
    setTemplateDialog({ open: true, template, mode: 'edit' });
  };

  // Handle template save
  const handleSaveTemplate = () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.message) {
      toast.error('Please fill all required fields');
      return;
    }
    saveTemplate(templateForm);
  };

  // Handle send wish
  const handleSendWish = () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template');
      return;
    }
    sendWish({
      employeeId: wishDialog.celebration.employee_id,
      celebrationType: wishDialog.celebration.celebration_type,
      templateId: selectedTemplateId
    });
  };

  // Celebration card component
  const CelebrationCard = ({ celebration, showActions = true }) => {
    const typeConfig = CELEBRATION_TYPES[celebration.celebration_type] || CELEBRATION_TYPES.birthday;
    const Icon = typeConfig.icon;
    
    return (
      <div className={`flex items-center justify-between p-4 rounded-lg border ${typeConfig.bg}`}>
        <div className="flex items-center gap-4">
          <div className={`p-2 rounded-full bg-white ${typeConfig.color}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">{celebration.employee_name}</p>
            <p className="text-sm text-muted-foreground">{celebration.department} • {celebration.designation}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {typeConfig.emoji} {typeConfig.label}
              </Badge>
              {celebration.years_completed > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {celebration.years_completed} {celebration.celebration_type === 'birthday' ? 'years old' : 'years'}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium">
            {celebration.days_until === 0 ? 'Today!' : `In ${celebration.days_until} days`}
          </p>
          <p className="text-xs text-muted-foreground">{celebration.full_date}</p>
          {showActions && canManage && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => {
                setSelectedTemplateId('');
                setWishDialog({ open: true, celebration });
              }}
            >
              <Send className="h-3 w-3 mr-1" />
              Send Wish
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="celebrations-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PartyPopper className="h-7 w-7 text-yellow-500" />
            Celebrations
          </h1>
          <p className="text-muted-foreground">Birthdays, Marriage & Work Anniversaries</p>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <>
              <Button variant="outline" onClick={() => setSettingsDialog(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <Button variant="outline" onClick={() => { sendReminders(); }}>
                <Bell className="h-4 w-4 mr-2" />
                Send Reminders
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => { refreshUpcoming(); refreshStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today</p>
                <p className="text-2xl font-bold">{stats?.today?.birthday + stats?.today?.marriage_anniversary + stats?.today?.work_anniversary || 0}</p>
              </div>
              <Gift className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold">{stats?.next_7_days?.total || 0}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold">{stats?.next_30_days?.total || 0}</p>
              </div>
              <Users className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Wishes Sent</p>
                <p className="text-2xl font-bold">{stats?.wishes_sent_this_month || 0}</p>
              </div>
              <Send className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">
            <Calendar className="h-4 w-4 mr-2" />
            Upcoming
          </TabsTrigger>
          {canManage && (
            <>
              <TabsTrigger value="templates" data-testid="tab-templates">
                <FileText className="h-4 w-4 mr-2" />
                Templates
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <Gift className="h-4 w-4 mr-2" />
                History
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Upcoming Tab */}
        <TabsContent value="upcoming" className="mt-4 space-y-4">
          {/* Filters */}
          <div className="flex gap-4 items-center">
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Celebrations</SelectItem>
                <SelectItem value="birthday">🎂 Birthdays</SelectItem>
                <SelectItem value="marriage_anniversary">💍 Marriage Anniversary</SelectItem>
                <SelectItem value="work_anniversary">🎉 Work Anniversary</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(daysAhead)} onValueChange={(v) => setDaysAhead(Number(v))}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Days ahead" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Next 7 days</SelectItem>
                <SelectItem value="14">Next 14 days</SelectItem>
                <SelectItem value="30">Next 30 days</SelectItem>
                <SelectItem value="60">Next 60 days</SelectItem>
                <SelectItem value="90">Next 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loadingUpcoming ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Today */}
              {groupedUpcoming.today.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="text-2xl">🎉</span> Today's Celebrations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {groupedUpcoming.today.map((c, idx) => (
                      <CelebrationCard key={idx} celebration={c} />
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* This Week */}
              {groupedUpcoming.thisWeek.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">This Week</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {groupedUpcoming.thisWeek.map((c, idx) => (
                      <CelebrationCard key={idx} celebration={c} />
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Later */}
              {groupedUpcoming.later.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Coming Up</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {groupedUpcoming.later.map((c, idx) => (
                      <CelebrationCard key={idx} celebration={c} />
                    ))}
                  </CardContent>
                </Card>
              )}

              {groupedUpcoming.today.length === 0 && groupedUpcoming.thisWeek.length === 0 && groupedUpcoming.later.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No upcoming celebrations in the selected period</p>
                    <p className="text-sm">Try increasing the days ahead filter</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        {canManage && (
          <TabsContent value="templates" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Wish Templates</CardTitle>
                    <CardDescription>Manage celebration wish templates</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {(!templates || templates.length === 0) && (
                      <Button variant="outline" onClick={() => initializeTemplates()} disabled={initializing}>
                        {initializing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Initialize Defaults
                      </Button>
                    )}
                    <Button onClick={() => {
                      setTemplateForm({ name: '', celebration_type: 'birthday', subject: '', message: '' });
                      setTemplateDialog({ open: true, template: null, mode: 'create' });
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      New Template
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingTemplates ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(CELEBRATION_TYPES).map(([type, config]) => (
                      <div key={type}>
                        <h3 className={`font-medium mb-3 flex items-center gap-2 ${config.color}`}>
                          <config.icon className="h-5 w-5" />
                          {config.label} Templates
                        </h3>
                        {(templatesByType[type] || []).length > 0 ? (
                          <div className="space-y-2">
                            {templatesByType[type].map(t => (
                              <div key={t.id} className="flex items-center justify-between p-3 border rounded-lg">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{t.name}</p>
                                    {t.is_default && <Badge variant="secondary">Default</Badge>}
                                    {!t.is_active && <Badge variant="destructive">Inactive</Badge>}
                                  </div>
                                  <p className="text-sm text-muted-foreground">{t.subject}</p>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="ghost" onClick={() => handleEditTemplate(t)}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => deleteTemplate(t.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No templates for this type</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* History Tab */}
        {canManage && (
          <TabsContent value="history" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Wish History</CardTitle>
                <CardDescription>Log of sent celebration wishes</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-center py-8">
                  Wish history will appear here after wishes are sent.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={templateDialog.open} onOpenChange={(open) => setTemplateDialog({ ...templateDialog, open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{templateDialog.mode === 'create' ? 'Create' : 'Edit'} Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Birthday - Warm Wishes"
                />
              </div>
              <div className="space-y-2">
                <Label>Celebration Type</Label>
                <Select
                  value={templateForm.celebration_type}
                  onValueChange={(v) => setTemplateForm({ ...templateForm, celebration_type: v })}
                  disabled={templateDialog.mode === 'edit'}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="birthday">🎂 Birthday</SelectItem>
                    <SelectItem value="marriage_anniversary">💍 Marriage Anniversary</SelectItem>
                    <SelectItem value="work_anniversary">🎉 Work Anniversary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="e.g., 🎂 Happy Birthday, {employee_name}!"
              />
              <p className="text-xs text-muted-foreground">Use {'{employee_name}'}, {'{years}'} as placeholders</p>
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={templateForm.message}
                onChange={(e) => setTemplateForm({ ...templateForm, message: e.target.value })}
                placeholder="Write your celebration message..."
                rows={8}
              />
              <p className="text-xs text-muted-foreground">
                Placeholders: {'{employee_name}'}, {'{years}'}, {'{department}'}, {'{designation}'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialog({ open: false, template: null, mode: 'create' })}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
              {savingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {templateDialog.mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Wish Dialog */}
      <Dialog open={wishDialog.open} onOpenChange={(open) => setWishDialog({ ...wishDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Celebration Wish</DialogTitle>
            <DialogDescription>
              Send a {wishDialog.celebration?.celebration_type?.replace('_', ' ')} wish to {wishDialog.celebration?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{wishDialog.celebration?.employee_name}</p>
              <p className="text-sm text-muted-foreground">{wishDialog.celebration?.department}</p>
              <Badge className="mt-2">
                {CELEBRATION_TYPES[wishDialog.celebration?.celebration_type]?.emoji} {CELEBRATION_TYPES[wishDialog.celebration?.celebration_type]?.label}
              </Badge>
            </div>
            <div className="space-y-2">
              <Label>Select Template</Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {(templatesByType[wishDialog.celebration?.celebration_type] || []).map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} {t.is_default && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWishDialog({ open: false, celebration: null })}>
              Cancel
            </Button>
            <Button onClick={handleSendWish} disabled={sendingWish || !selectedTemplateId}>
              {sendingWish ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Wish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Celebration Settings</DialogTitle>
            <DialogDescription>Configure automatic reminders and wishes</DialogDescription>
          </DialogHeader>
          {settings && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Send reminders to HR</Label>
                  <p className="text-sm text-muted-foreground">HR will receive celebration reminders</p>
                </div>
                <Switch checked={settings.send_to_hr} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Send reminders to Managers</Label>
                  <p className="text-sm text-muted-foreground">Reporting managers get team reminders</p>
                </div>
                <Switch checked={settings.send_to_reporting_manager} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-send wishes to employees</Label>
                  <p className="text-sm text-muted-foreground">Automatically send wishes on the day</p>
                </div>
                <Switch checked={settings.send_wishes_to_employee} />
              </div>
              <div className="space-y-2">
                <Label>Reminder days before</Label>
                <Select value={String(settings.reminder_days_before || 1)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Same day</SelectItem>
                    <SelectItem value="1">1 day before</SelectItem>
                    <SelectItem value="2">2 days before</SelectItem>
                    <SelectItem value="3">3 days before</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
